/**
 * ProtectedRoute.tsx
 *
 * Fixes vs original:
 *  1. On first render after a hard reload, the zustand store takes one tick to
 *     rehydrate from localStorage. During that tick isAuthenticated is false,
 *     so the original component instantly redirected to /admin/login before the
 *     real token check finished. Fixed with a short loading gate.
 *  2. Role guard added: only 'admin' and 'superadmin' can pass. Any other role
 *     that somehow holds a token is bounced back to /admin/login.
 */

import { useState, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { useAdminAuth } from '@/store/authStore'

interface ProtectedRouteProps {
  children: React.ReactNode
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, user, hasRehydrated } = useAdminAuth()
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    // Grace period for rehydration to settle — prevents bounce on hard refresh
    const timer = setTimeout(() => setIsReady(true), 100)
    return () => clearTimeout(timer)
  }, [])

  if (!isReady || !hasRehydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />
  }

  // Role guard — belt-and-suspenders (server enforces this too)
  if (user && user.role !== 'admin' && user.role !== 'superadmin') {
    return <Navigate to="/admin/login" replace />
  }

  return <>{children}</>
}
